import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingBag, MapPin, ExternalLink, CheckCircle2, Camera, Star } from "lucide-react";
import { toast } from "sonner";
import OrderTimeline, { stageLabel } from "@/components/OrderTimeline";
import ProofOfDeliveryDialog from "@/components/ProofOfDeliveryDialog";
import LeaveReviewDialog from "@/components/LeaveReviewDialog";

const Purchases = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofFor, setProofFor] = useState(null); // tx_id when proof dialog is open
  // When set, review dialog opens. `auto` flag styles the copy as a post-delivery prompt.
  const [reviewFor, setReviewFor] = useState(null); // { listing, sellerName, auto }
  const { formatPrice } = useCurrency();

  const load = () => {
    setLoading(true);
    api.get("/me/purchases")
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Could not load your orders"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Right after proof-of-delivery upload, automatically prompt for a review.
  // This is the highest-converting moment: buyer just engaged + has the item.
  const handleProofSubmitted = (order) => {
    load();
    if (order?.listing && !order.has_reviewed) {
      setReviewFor({
        listing: order.listing,
        sellerName: order.listing.owner_name,
        auto: true,
      });
    }
  };

  return (
    <div className="container-narrow py-10" data-testid="purchases-page">
      <div className="mb-8">
        <div className="label-overline text-[#C05A3C] mb-2">My Account</div>
        <h1 className="font-display text-4xl tracking-tight">Your Orders</h1>
        <p className="text-muted-foreground mt-2 font-sans-ui">
          Track every order from payment through to delivery.
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center bg-white border border-black/10 rounded-lg" data-testid="purchases-empty">
          <ShoppingBag className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-display text-2xl">No orders yet</h3>
          <p className="text-muted-foreground mt-2">Browse the marketplace and your purchases will show up here.</p>
          <Link to="/browse" className="mt-5 inline-block text-[#C05A3C] hover:underline font-semibold">
            Start browsing →
          </Link>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => {
            const img = o.listing?.images?.[0];
            const failed = o.status === "failed" || o.status === "expired";
            return (
              <div
                key={o.transaction_id || o.session_id}
                className="bg-white border border-black/10 rounded-lg overflow-hidden"
                data-testid={`order-row-${o.transaction_id || o.session_id}`}
              >
                <div className="flex flex-col sm:flex-row gap-4 p-5 border-b border-black/5">
                  {img && (
                    <Link to={`/listing/${o.listing_id}`} className="shrink-0">
                      <img
                        src={img}
                        alt={o.listing?.title || "Order"}
                        className="w-full sm:w-28 h-28 object-cover rounded-md"
                      />
                    </Link>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <Link to={`/listing/${o.listing_id}`} className="font-display text-xl leading-tight hover:text-[#C05A3C] transition-colors">
                          {o.listing?.title || "Listing"}
                        </Link>
                        {o.listing && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <MapPin className="w-3 h-3" />
                            {o.listing.city ? `${o.listing.city}, ` : ""}{o.listing.country}
                            {o.listing.owner_name && <> · sold by <span className="font-semibold">{o.listing.owner_name}</span></>}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl text-[#C05A3C]">
                          {formatPrice(o.amount, o.currency) || `${o.currency || ""} ${o.amount}`}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                          {failed ? "Payment failed" : stageLabel(o.stage)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-5 sm:py-6">
                  {failed ? (
                    <div className="text-sm text-[#C05A3C] bg-[#C05A3C]/10 rounded-md p-3" data-testid="order-failed-msg">
                      This payment did not complete. Try again from the listing — your card was not charged.
                    </div>
                  ) : (
                    <OrderTimeline
                      stage={o.stage}
                      paidAt={o.paid_at}
                      shippedAt={o.shipped_at}
                      deliveredAt={o.delivered_at}
                      trackingUrl={o.tracking_url}
                    />
                  )}
                  {/* Tracking details */}
                  {(o.tracking_number || o.tracking_url) && !failed && (
                    <div className="mt-4 bg-[#F3EFE9] rounded-md px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="tracking-block">
                      {o.tracking_carrier && <span className="font-semibold">{o.tracking_carrier}</span>}
                      {o.tracking_number && <span className="font-mono">{o.tracking_number}</span>}
                      {o.tracking_url && (
                        <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C05A3C] hover:underline font-semibold">
                          <ExternalLink className="w-3 h-3" /> Track parcel
                        </a>
                      )}
                      <Link
                        to={`/orders/${o.transaction_id || o.session_id}/track`}
                        data-testid={`view-tracking-page-${o.transaction_id || o.session_id}`}
                        className="ml-auto inline-flex items-center gap-1 text-[#1A2024] hover:text-[#C05A3C] font-semibold underline"
                      >
                        View full timeline →
                      </Link>
                    </div>
                  )}
                  {/* Always-visible "Track order" link — even before tracking number exists, the timeline page is useful */}
                  {!failed && !o.tracking_number && (
                    <div className="mt-3 text-xs text-right">
                      <Link
                        to={`/orders/${o.transaction_id || o.session_id}/track`}
                        data-testid={`view-tracking-page-${o.transaction_id || o.session_id}`}
                        className="text-[#C05A3C] hover:underline font-semibold"
                      >
                        View live tracking timeline →
                      </Link>
                    </div>
                  )}
                  {/* Buyer confirms delivery — only show once shipped, not yet confirmed */}
                  {o.stage === "shipped" && !o.delivery_confirmed_by_buyer && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-4">
                      <div className="text-xs text-muted-foreground max-w-md">
                        Received your order? Confirm delivery and upload a photo for our records.
                      </div>
                      <Button
                        data-testid={`confirm-delivery-btn-${o.transaction_id || o.session_id}`}
                        onClick={() => setProofFor(o)}
                        className="bg-[#2A3D36] hover:bg-[#1F2D27] text-white"
                      >
                        <Camera className="w-4 h-4 mr-2" /> Confirm delivery
                      </Button>
                    </div>
                  )}
                  {/* Leave a review — appears on delivered orders without a review yet */}
                  {o.stage === "delivered" && !o.has_reviewed && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-4 bg-[#D9923B]/8 -mx-5 px-5 py-3 sm:-mx-6 sm:px-6">
                      <div className="text-xs text-[#1A1A1A] max-w-md">
                        <strong>Loved your order?</strong> A 1-line review helps the next buyer and lifts the seller&apos;s ranking.
                      </div>
                      <Button
                        data-testid={`leave-review-btn-${o.transaction_id || o.session_id}`}
                        onClick={() => setReviewFor({ listing: o.listing, sellerName: o.listing?.owner_name, auto: false })}
                        className="bg-[#D9923B] hover:bg-[#C0822F] text-white"
                      >
                        <Star className="w-4 h-4 mr-2 fill-white" /> Leave a review
                      </Button>
                    </div>
                  )}
                  {/* Proof of delivery photo (once confirmed) */}
                  {o.delivery_proof_url && (
                    <div className="mt-4 border-t border-black/5 pt-4" data-testid="proof-display">
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#2A3D36] mb-2 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Delivery confirmed by you
                      </div>
                      <img src={o.delivery_proof_url} alt="Delivery proof" className="rounded-md max-h-44 object-cover" />
                      {o.delivery_proof_note && (
                        <div className="text-xs text-muted-foreground mt-2 italic">&ldquo;{o.delivery_proof_note}&rdquo;</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ProofOfDeliveryDialog
        open={!!proofFor}
        txId={proofFor?.transaction_id || proofFor?.session_id}
        onClose={() => setProofFor(null)}
        onSubmitted={() => handleProofSubmitted(proofFor)}
      />
      <LeaveReviewDialog
        open={!!reviewFor}
        listing={reviewFor?.listing}
        sellerName={reviewFor?.sellerName}
        autoTriggered={!!reviewFor?.auto}
        onClose={() => setReviewFor(null)}
        onSubmitted={load}
      />
    </div>
  );
};

export default Purchases;
